// Předrenderované stránky webu a jejich statická hlavička (FR01 20261001_LandingPage/002_Marketing,
// FR-03, FR-05). Modul je čistý (bez Reactu, CSS a `window`), protože ho používá klientský vstup,
// SSR vstup i vite.config.ts (náhled hlavičky na vývojovém serveru).

export type PagePath = '/' | '/cookies';

export interface PageHead {
  /** Cesta souboru v dist/ relativně ke kořeni. */
  file: string;
  title: string;
  description: string;
  canonical: string;
}

export const SITE_ORIGIN = 'https://trenigo.cz';

export const PAGES: Record<PagePath, PageHead> = {
  // Title a description ve stejném znění, jaké dřív za běhu nastavoval useLandingSeo v apps/web.
  '/': {
    file: 'index.html',
    title: 'Trenigo – aplikace pro trenéry a sportovní akademie',
    description:
      'Trenigo spojuje docházku, kredity, platby, tréninky, svěřence a mzdy trenérů do jednoho přehledného systému pro sportovní akademie.',
    canonical: `${SITE_ORIGIN}/`,
  },
  '/cookies': {
    file: 'cookies/index.html',
    title: 'Cookies a ochrana osobních údajů – Trenigo',
    description:
      'Přehled cookies, které Trenigo používá, a informace o zpracování osobních údajů. Používáme jen cookies nezbytné pro chod aplikace.',
    canonical: `${SITE_ORIGIN}/cookies`,
  },
};

export const PAGE_PATHS = Object.keys(PAGES) as PagePath[];

/**
 * Stránka pro cestu z adresního řádku. Neznámé cesty (např. starý odkaz https://trenigo.cz/login)
 * servíruje Netlify přes `_redirects` jako index.html se stavem 200, proto se na ně vykresluje
 * landing page - stejně jako předrenderované HTML, které klient hydratuje.
 */
export function resolvePagePath(pathname: string): PagePath {
  const normalized = pathname.replace(/\/index\.html$/, '/').replace(/\/+$/, '') || '/';
  return normalized === '/cookies' ? '/cookies' : '/';
}

const HTML_ESCAPES: Record<string, string> = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };

export function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (char) => HTML_ESCAPES[char] ?? char);
}

/** Značky hlavičky, které se liší podle stránky; společné značky jsou v index.html. */
export function renderHeadTags(path: PagePath): string {
  const page = PAGES[path];
  return [
    `<title>${escapeHtml(page.title)}</title>`,
    `<meta name="description" content="${escapeHtml(page.description)}" />`,
    `<link rel="canonical" href="${escapeHtml(page.canonical)}" />`,
  ].join('\n    ');
}

export const HEAD_PLACEHOLDER = '<!--app-head-->';
export const HTML_PLACEHOLDER = '<!--app-html-->';

/** Složí výsledné HTML stránky ze šablony (dist/index.html po klientském buildu). */
export function composePageHtml(template: string, path: PagePath, appHtml: string): string {
  if (!template.includes(HEAD_PLACEHOLDER) || !template.includes(HTML_PLACEHOLDER)) {
    throw new Error(`Šablona index.html neobsahuje ${HEAD_PLACEHOLDER} nebo ${HTML_PLACEHOLDER}.`);
  }
  // Funkce jako náhrada, aby se znaky "$" v obsahu nevykládaly jako vzory String.replace.
  return template.replace(HEAD_PLACEHOLDER, () => renderHeadTags(path)).replace(HTML_PLACEHOLDER, () => appHtml);
}
